import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { AsyncSearchSelect } from '@/components/AsyncSearchSelect';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LeadIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const TaskIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const ContactIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const CompanyIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const ActivityIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const ReminderIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const quickCreateOptions = [
  { id: 'lead', label: 'Lead', icon: LeadIcon, color: 'bg-blue-500' },
  { id: 'task', label: 'Task', icon: TaskIcon, color: 'bg-purple-500' },
  { id: 'contact', label: 'Contact', icon: ContactIcon, color: 'bg-green-500' },
  { id: 'company', label: 'Company', icon: CompanyIcon, color: 'bg-amber-500' },
  { id: 'activity', label: 'Activity', icon: ActivityIcon, color: 'bg-cyan-500' },
  { id: 'reminder', label: 'Reminder', icon: ReminderIcon, color: 'bg-rose-500' },
];

const INDUSTRIES = ['E-commerce', 'Technology', 'Retail', 'Healthcare', 'Finance', 'Manufacturing', 'Education', 'Real Estate', 'Consulting', 'Media & Entertainment', 'Transportation', 'Hospitality', 'Telecommunications', 'Other'];
const SOURCES = ['Website', 'Contact Form', 'Get a Demo', 'Get a Call Back', 'LinkedIn', 'Referral', 'Cold Call', 'Email Campaign', 'Conference', 'Social Media', 'Self', 'Marketing', 'Internal Ref', 'Client Reference', 'Existing Client', 'Churned Client', 'Partner- India', 'Partner- Intl', 'OEM-VISPL', 'OEM-OXT', 'Other'];
const DESIGNATIONS = ['CEO', 'CTO', 'CFO', 'COO', 'VP Sales', 'VP Marketing', 'Director', 'Manager', 'Team Lead', 'Executive', 'Analyst', 'Other'];
const ACTIVITY_TYPES = ['call', 'email', 'meeting', 'follow_up', 'note'];
const TASK_TYPES = ['call', 'follow_up', 'meeting', 'email', 'other'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

function QuickCreateFAB() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fabRef = useRef(null);
  
  // Draggable FAB position - always start at default position (bottom right)
  const [fabPosition, setFabPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Handle drag start
  const handleDragStart = (e) => {
    if (isOpen) return; // Don't drag when menu is open
    e.preventDefault();
    setIsDragging(true);
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    const rect = fabRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    // Calculate new position
    let newX = clientX - dragOffset.current.x;
    let newY = clientY - dragOffset.current.y;
    
    // Keep within viewport bounds
    const fabSize = 48; // w-12 h-12 = 48px
    const padding = 16;
    newX = Math.max(padding, Math.min(window.innerWidth - fabSize - padding, newX));
    newY = Math.max(padding, Math.min(window.innerHeight - fabSize - padding, newY));
    
    setFabPosition({ x: newX, y: newY });
  }, [isDragging]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Position is temporary - will reset on page refresh
    }
  }, [isDragging]);

  // Add/remove drag event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Reset FAB position on double-click
  const handleDoubleClick = () => {
    setFabPosition({ x: null, y: null });
  };

  // Helper to navigate and force page refresh
  const navigateWithRefresh = (path, filter = null) => {
    const state = { refresh: Date.now(), filter };
    if (location.pathname === path) {
      // If already on the same page, force a refresh by navigating away and back
      navigate('/dashboard', { replace: true });
      setTimeout(() => navigate(path, { replace: true, state }), 50);
    } else {
      navigate(path, { state });
    }
  };
  
  // Data for dropdowns (contacts is still prefetched; leads/companies are on-demand)
  const [contacts, setContacts] = useState([]);
  
  // Form states
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', organization: '', industry: '', source: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', task_type: 'other', priority: 'medium', due_date: '', lead_id: '', lead_label: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', company_id: '', company_label: '', designation: '' });
  const [companyForm, setCompanyForm] = useState({ name: '', industry: '', website: '', phone: '' });
  const [activityForm, setActivityForm] = useState({ activity_type: 'call', lead_id: '', lead_label: '', notes: '', outcome: '' });
  const [reminderForm, setReminderForm] = useState({ title: '', due_date: '', lead_id: '', lead_label: '', priority: 'medium' });

  // Fetch data for dropdowns
  const fetchDropdownData = useCallback(async () => {
    try {
      // Only fetch contacts for the contact form auto-complete; leads & companies
      // are resolved on-demand via AsyncSearchSelect now (was `?limit=100`).
      const contactsRes = await axios.get(`${API}/contacts?limit=100`);
      setContacts(contactsRes.data || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  // Async fetchers for AsyncSearchSelect
  const fetchLeadOptions = async (q) => {
    try {
      const res = await axios.get(`${API}/leads`, { params: { search: q, page: 1, limit: 20 } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      return data;
    } catch { return []; }
  };
  const fetchCompanyOptions = async (q) => {
    try {
      const res = await axios.get(`${API}/companies`, { params: { search: q, page: 1, limit: 20 } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      return data;
    } catch { return []; }
  };

  useEffect(() => {
    if (activeModal) {
      fetchDropdownData();
    }
  }, [activeModal, fetchDropdownData]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fabRef.current && !fabRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Helper to extract error message from API response
  const getErrorMessage = (error, defaultMsg) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      // Pydantic validation error format
      return detail[0]?.msg || defaultMsg;
    }
    return defaultMsg;
  };

  const resetForms = () => {
    setLeadForm({ name: '', email: '', phone: '', organization: '', industry: '', source: '' });
    setTaskForm({ title: '', description: '', task_type: 'other', priority: 'medium', due_date: '', lead_id: '', lead_label: '' });
    setContactForm({ name: '', email: '', phone: '', company_id: '', company_label: '', designation: '' });
    setCompanyForm({ name: '', industry: '', website: '', phone: '' });
    setActivityForm({ activity_type: 'call', lead_id: '', lead_label: '', notes: '', outcome: '' });
    setReminderForm({ title: '', due_date: '', lead_id: '', lead_label: '', priority: 'medium' });
  };

  const handleOptionClick = (optionId) => {
    setIsOpen(false);
    setActiveModal(optionId);
    resetForms();
  };

  const closeModal = () => {
    setActiveModal(null);
    resetForms();
    setMessage({ type: '', text: '' });
  };

  // Helper to clean form data - convert empty strings to null and strip UI-only *_label fields
  const cleanFormData = (data) => {
    const cleaned = {};
    for (const [key, value] of Object.entries(data)) {
      if (key.endsWith('_label')) continue;
      cleaned[key] = value === '' ? null : value;
    }
    return cleaned;
  };

  // Create handlers
  const createLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name) { showMessage('error', 'Name is required'); return; }
    
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...cleanFormData(leadForm),
        status: 'new'
      });
      showMessage('success', 'Lead created successfully!');
      setTimeout(() => {
        closeModal();
        navigateWithRefresh('/leads');
      }, 1000);
    } catch (error) {
      showMessage('error', getErrorMessage(error, 'Failed to create lead'));
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title) { showMessage('error', 'Title is required'); return; }
    
    setLoading(true);
    try {
      await axios.post(`${API}/tasks`, {
        ...cleanFormData(taskForm),
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null
      });
      showMessage('success', 'Task created successfully!');
      setTimeout(() => {
        closeModal();
        // Navigate to tasks with filter set to show all or appropriate filter
        const hasDate = !!taskForm.due_date;
        navigateWithRefresh('/tasks', hasDate ? 'today' : 'all');
      }, 1000);
    } catch (error) {
      showMessage('error', getErrorMessage(error, 'Failed to create task'));
    } finally {
      setLoading(false);
    }
  };

  const createContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name) { showMessage('error', 'Name is required'); return; }
    if (!contactForm.company_id) { showMessage('error', 'Company is required'); return; }
    
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, cleanFormData(contactForm));
      showMessage('success', 'Contact created successfully!');
      setTimeout(() => {
        closeModal();
        navigateWithRefresh('/contacts');
      }, 1000);
    } catch (error) {
      showMessage('error', getErrorMessage(error, 'Failed to create contact'));
    } finally {
      setLoading(false);
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name) { showMessage('error', 'Company name is required'); return; }
    
    setLoading(true);
    try {
      await axios.post(`${API}/companies`, cleanFormData(companyForm));
      showMessage('success', 'Company created successfully!');
      setTimeout(() => {
        closeModal();
        navigateWithRefresh('/companies');
      }, 1000);
    } catch (error) {
      showMessage('error', getErrorMessage(error, 'Failed to create company'));
    } finally {
      setLoading(false);
    }
  };

  const createActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.lead_id) { showMessage('error', 'Lead is required'); return; }
    
    setLoading(true);
    try {
      await axios.post(`${API}/activities`, {
        ...cleanFormData(activityForm),
        timestamp: new Date().toISOString()
      });
      showMessage('success', 'Activity logged successfully!');
      setTimeout(() => {
        closeModal();
        navigate(`/leads/${activityForm.lead_id}`);
      }, 1000);
    } catch (error) {
      showMessage('error', getErrorMessage(error, 'Failed to log activity'));
    } finally {
      setLoading(false);
    }
  };

  const createReminder = async (e) => {
    e.preventDefault();
    if (!reminderForm.title) { showMessage('error', 'Title is required'); return; }
    if (!reminderForm.due_date) { showMessage('error', 'Due date is required'); return; }
    
    setLoading(true);
    try {
      // Create as a task with reminder type
      await axios.post(`${API}/tasks`, {
        title: reminderForm.title,
        task_type: 'follow_up',
        priority: reminderForm.priority,
        lead_id: reminderForm.lead_id || null,
        due_date: new Date(reminderForm.due_date).toISOString(),
        reminder: '1_hour'
      });
      showMessage('success', 'Reminder created successfully!');
      setTimeout(() => {
        closeModal();
        // Navigate to tasks - since reminder has due date, go to today view
        navigateWithRefresh('/tasks', 'today');
      }, 1000);
    } catch (error) {
      showMessage('error', getErrorMessage(error, 'Failed to create reminder'));
    } finally {
      setLoading(false);
    }
  };

  // Modal content based on type
  const renderModalContent = () => {
    switch (activeModal) {
      case 'lead':
        return (
          <form onSubmit={createLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="John Doe"
                  required
                  data-testid="quick-lead-name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Organization</label>
                <input
                  type="text"
                  value={leadForm.organization}
                  onChange={(e) => setLeadForm({ ...leadForm, organization: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="Acme Inc"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Industry</label>
                <select
                  value={leadForm.industry}
                  onChange={(e) => setLeadForm({ ...leadForm, industry: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Source</label>
                <select
                  value={leadForm.source}
                  onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select</option>
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {renderFormFooter()}
          </form>
        );

      case 'task':
        return (
          <form onSubmit={createTask} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Title *</label>
              <input
                type="text"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                placeholder="Follow up with client"
                required
                data-testid="quick-task-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Type</label>
                <select
                  value={taskForm.task_type}
                  onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  {TASK_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Priority</label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Due Date</label>
                <input
                  type="datetime-local"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Related Lead</label>
                <AsyncSearchSelect
                  value={taskForm.lead_id}
                  valueLabel={taskForm.lead_label}
                  onSelect={(l) => {
                    if (!l) setTaskForm(p => ({ ...p, lead_id: '', lead_label: '' }));
                    else setTaskForm(p => ({ ...p, lead_id: l.id, lead_label: `${l.name}${l.organization ? ` (${l.organization})` : ''}` }));
                  }}
                  fetchOptions={fetchLeadOptions}
                  placeholder="Search leads…"
                  testId="quick-task-lead-search"
                  renderOption={(l) => (
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900 dark:text-white truncate">{l.name}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{l.organization || l.email || ''}</span>
                    </div>
                  )}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Description</label>
              <textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                rows={2}
                placeholder="Add notes..."
              />
            </div>
            {renderFormFooter()}
          </form>
        );

      case 'contact':
        return (
          <form onSubmit={createContact} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Company *</label>
              <AsyncSearchSelect
                value={contactForm.company_id}
                valueLabel={contactForm.company_label}
                onSelect={(c) => {
                  if (!c) setContactForm(p => ({ ...p, company_id: '', company_label: '' }));
                  else setContactForm(p => ({ ...p, company_id: c.id, company_label: c.name }));
                }}
                fetchOptions={fetchCompanyOptions}
                placeholder="Search companies…"
                testId="quick-contact-company-search"
                renderOption={(c) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate">{c.name}</span>
                    {(c.industry || c.city) && <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{c.industry || ''}{c.industry && c.city ? ' • ' : ''}{c.city || ''}</span>}
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="Jane Smith"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="jane@company.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Designation</label>
                <select
                  value={contactForm.designation}
                  onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select</option>
                  {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            {renderFormFooter()}
          </form>
        );

      case 'company':
        return (
          <form onSubmit={createCompany} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Company Name *</label>
              <input
                type="text"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                placeholder="Acme Corporation"
                required
                data-testid="quick-company-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Industry</label>
                <select
                  value={companyForm.industry}
                  onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={companyForm.phone}
                  onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="+91 22 1234 5678"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Website</label>
                <input
                  type="url"
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="https://company.com"
                />
              </div>
            </div>
            {renderFormFooter()}
          </form>
        );

      case 'activity':
        return (
          <form onSubmit={createActivity} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Type *</label>
                <select
                  value={activityForm.activity_type}
                  onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="quick-activity-type"
                >
                  {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Lead *</label>
                <AsyncSearchSelect
                  value={activityForm.lead_id}
                  valueLabel={activityForm.lead_label}
                  onSelect={(l) => {
                    if (!l) setActivityForm(p => ({ ...p, lead_id: '', lead_label: '' }));
                    else setActivityForm(p => ({ ...p, lead_id: l.id, lead_label: `${l.name}${l.organization ? ` (${l.organization})` : ''}` }));
                  }}
                  fetchOptions={fetchLeadOptions}
                  placeholder="Search leads…"
                  testId="quick-activity-lead-search"
                  renderOption={(l) => (
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900 dark:text-white truncate">{l.name}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{l.organization || l.email || ''}</span>
                    </div>
                  )}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Notes</label>
              <textarea
                value={activityForm.notes}
                onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                rows={3}
                placeholder="Add notes about this activity..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Outcome</label>
              <input
                type="text"
                value={activityForm.outcome}
                onChange={(e) => setActivityForm({ ...activityForm, outcome: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                placeholder="e.g., Scheduled demo, Left voicemail"
              />
            </div>
            {renderFormFooter()}
          </form>
        );

      case 'reminder':
        return (
          <form onSubmit={createReminder} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Reminder Title *</label>
              <input
                type="text"
                value={reminderForm.title}
                onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                placeholder="Call back customer"
                required
                data-testid="quick-reminder-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Due Date *</label>
                <input
                  type="datetime-local"
                  value={reminderForm.due_date}
                  onChange={(e) => setReminderForm({ ...reminderForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Priority</label>
                <select
                  value={reminderForm.priority}
                  onChange={(e) => setReminderForm({ ...reminderForm, priority: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Related Lead</label>
              <AsyncSearchSelect
                value={reminderForm.lead_id}
                valueLabel={reminderForm.lead_label}
                onSelect={(l) => {
                  if (!l) setReminderForm(p => ({ ...p, lead_id: '', lead_label: '' }));
                  else setReminderForm(p => ({ ...p, lead_id: l.id, lead_label: `${l.name}${l.organization ? ` (${l.organization})` : ''}` }));
                }}
                fetchOptions={fetchLeadOptions}
                placeholder="Search leads…"
                testId="quick-reminder-lead-search"
                renderOption={(l) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate">{l.name}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{l.organization || l.email || ''}</span>
                  </div>
                )}
              />
            </div>
            {renderFormFooter()}
          </form>
        );

      default:
        return null;
    }
  };

  const renderFormFooter = () => (
    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
      <button
        type="button"
        onClick={closeModal}
        className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 transition flex items-center gap-2"
        data-testid="quick-create-submit"
      >
        {loading && (
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        Create
      </button>
    </div>
  );

  const getModalTitle = () => {
    const option = quickCreateOptions.find(o => o.id === activeModal);
    return option ? `Quick Create ${option.label}` : '';
  };

  return (
    <>
      {/* FAB Button and Menu - Draggable */}
      <div 
        ref={fabRef} 
        className="fixed z-50" 
        style={fabPosition.x !== null ? {
          left: fabPosition.x,
          top: fabPosition.y,
          right: 'auto',
          bottom: 'auto'
        } : {
          right: 24,
          bottom: 24
        }}
        data-testid="quick-create-fab"
      >
        {/* Options Menu */}
        <div className={`absolute ${fabPosition.x !== null ? 'bottom-14' : 'bottom-14'} right-0 transition-all duration-300 ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
          <div className="flex flex-col gap-2 items-end">
            {quickCreateOptions.map((option, index) => (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option.id)}
                className="flex items-center gap-2 group"
                style={{ transitionDelay: isOpen ? `${index * 50}ms` : '0ms' }}
                data-testid={`quick-create-${option.id}`}
              >
                <span className="px-2.5 py-1 bg-slate-800 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
                  {option.label}
                </span>
                <div className={`w-10 h-10 ${option.color} rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform`}>
                  <option.icon />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main FAB Button - Smaller and Draggable */}
        <button
          onClick={() => !isDragging && setIsOpen(!isOpen)}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onDoubleClick={handleDoubleClick}
          className={`w-12 h-12 bg-slate-900 text-white rounded-full shadow-xl flex items-center justify-center hover:bg-slate-800 transition-all duration-300 ${isOpen ? 'rotate-45' : 'rotate-0'} ${isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab'}`}
          data-testid="fab-toggle-btn"
          title="Drag to move, double-click to reset position"
        >
          <PlusIcon />
        </button>
      </div>

      {/* Modal Overlay */}
      {activeModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" 
            onClick={closeModal}
            data-testid="modal-overlay"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="quick-create-modal">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{getModalTitle()}</h3>
              <button 
                onClick={closeModal}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition text-slate-600 dark:text-slate-400"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Message */}
            {message.text && (
              <div className={`mx-4 mt-4 px-4 py-3 rounded-lg text-sm ${
                message.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400' 
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
              }`}>
                {message.text}
              </div>
            )}

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {renderModalContent()}
            </div>
          </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-in-right { 
          from { transform: translateX(100%); } 
          to { transform: translateX(0); } 
        }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </>
  );
}

export default QuickCreateFAB;
